import BaseObject from "../../../base/base_object";
import ServiceFactory from "../../service_factory";
import _ from "../../../utils/underscore";
import {IFirebaseParams} from "./firebase_strategy";
import {IBaseParams, ISubscriptionParams} from "./firebase_const";
import {ITriple8MessagingConnection} from "./triple8-messaging.interfaces";

export interface FirebaseHandlerInitParams{
  firebaseMessaging: ITriple8MessagingConnection;
  config: IFirebaseParams;
}


export default abstract class FirebaseHandlerBase extends BaseObject{
  static CLASS = "FirebaseHandlerBase";

  protected serviceFactory: ServiceFactory;
  protected firebaseMessaging: ITriple8MessagingConnection;
  protected enabled: boolean = false;
  protected forceFallback: boolean = false;
  private _subscriptionParams: ISubscriptionParams;

  protected init(params: FirebaseHandlerInitParams) {
    const optionalParams = params.config[this.getFeatureName()];
    if(optionalParams){
      this.setFeatureParams(optionalParams);
    }
    super.init(params);
    if(!params.config.serviceFactory) {
      this.throwArgumentExpected('30', 'init', 'serviceFactory');
    }

    if(!params.firebaseMessaging) {
      this.throwArgumentExpected('34', 'init', 'firebaseMessaging');
    }
    this.serviceFactory = params.config.serviceFactory;
    this.firebaseMessaging = params.firebaseMessaging;
    this._subscriptionParams = this.getSubscriptionParams();


  }

  protected abstract getFeatureName(): string;

  protected abstract getFeatureLabel(): string;

  protected abstract getFallbackServiceName(): string;

  protected abstract getSubscriptionParams(): ISubscriptionParams;

  protected setFeatureParams(params: IBaseParams){
    this.enabled = _.isNullOrUndefined(params.enabled) ? this.enabled : _.toBoolean(params.enabled);
    this.forceFallback = _.isNullOrUndefined(params.forceFallback) ? false : _.toBoolean(params.forceFallback);
  }

  async subscribe(): Promise<void> {
    if(this.enabled){
      if (this.forceFallback) {
        this.startFallback();
        this.logger.warn('Feature ' + this.getFeatureName() + ' is enabled, but forced to fallback.');
      } else {
        await this._startSubscriptionAsync();
      }
    } else {
      this.logger.warn('Feature ' + this.getFeatureName() + ' is disabled. Unable to receive message for this feature from Firebase');
    }
  }

  private _unsubscribeCallback: null | (() => void) = null;
  private async _startSubscriptionAsync() {
    try {
      if(this.firebaseMessaging.subscribeAsync) {
        this._unsubscribeCallback = await this.firebaseMessaging.subscribeAsync(
            this._subscriptionParams.eventName,
            this._dataReceiveCallback,
            this._subscriptionParams.params);
      } else {
        //TODO - this should be removed after unified team releases in RM the Triple8 library with async subscribe support. According to them this will happen on 26.06.2024
        this.firebaseMessaging.subscribe(
            this._subscriptionParams.eventName,
            this._dataReceiveCallback,
            (err) => {
              this.startFallback();
              this.logger.error('Firebase Handler error', err);
              this.serviceFactory.firebaseElasticService().sendSubscriptionError(this.getFeatureLabel(),JSON.stringify(err));
            },
            this._subscriptionParams.params);
        this._unsubscribeCallback = () => {}
      }


      this.logger.debug('Feature ' + this.getFeatureName() + ' is enabled. Able to receive message from Firebase');

    } catch (err) {
      this._onSubscribeFailed(err);
    }

  }

  private _dataReceiveCallback = (data: any) => {
    if(this.getFallbackServiceName()){
      this.serviceFactory.fallbackService().stop(this.getFallbackServiceName());
    }
    this._subscriptionParams.successCallback(data);
  }


  public unsubscribe(){
    if(this.getFallbackServiceName()){
      this.serviceFactory.fallbackService().stop(this.getFallbackServiceName());
    }

    if(this._unsubscribeCallback) {
      this._unsubscribeCallback();
      this._unsubscribeCallback = null;
    }
  }

  private _onSubscribeFailed = (err: any) => {
    this.startFallback();
    this.logger.error('Firebase Handler error', err);
    this.serviceFactory.firebaseElasticService().sendSubscriptionError(this.getFeatureLabel(),JSON.stringify(err));
  }

  protected startFallback(){
    if(this.serviceFactory.oAuth2Service().isAuthorized() && this.getFallbackServiceName()){
      this.serviceFactory.fallbackService().start(this.getFallbackServiceName());
      this.serviceFactory.firebaseElasticService().sendFallbackStarted(this.getFeatureLabel(), this.forceFallback);
    }
  }

}
